import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Box, Text } from 'braid-design-system';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import type { INotes } from '../../../stores/JobApplicationStore/Notes';
import {
  STATE_ERROR,
  STATE_LOADED,
  STATE_LOADING,
  ACTION_ERROR,
} from '../../../stores/state';
import ErrorAlert from '../../common/ErrorAlert';
import Loader from '../../common/Loader';

import translations from './.vocab';
import CreateNoteFormProvider from './CreateNoteForm';
import NoteItemList from './NoteItemList';

interface Props {
  notes: INotes;
  locale: Locale;
  jobId: string;
  applicationCorrelationId: string;
}

export const NOTES_MAX_CONTENT_LENGTH = 2000;

const Notes = ({ notes, jobId, applicationCorrelationId, locale }: Props) => {
  const { t } = useTranslations(translations);
  const [showServerError, setShowServerError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    notes.loadNotes({ jobId, applicationCorrelationId });
  }, [notes, jobId, applicationCorrelationId]);

  const noteList = notes.state !== STATE_ERROR && (
    <Box data-testid="notes" marginTop="large">
      {notes.state === STATE_LOADING && <Loader />}
      <NoteItemList notes={notes} locale={locale} />
    </Box>
  );

  const onAdd = (content: string) => {
    setLoading(true);
    notes.addNote(content).then((state: string) => {
      setShowServerError(state === ACTION_ERROR);
      setLoading(false);
    });
  };

  return (
    <>
      {notes.state === STATE_LOADED && (
        <Box marginBottom="medium">
          <CreateNoteFormProvider
            showServerError={showServerError}
            loading={loading}
            onAdd={onAdd}
            locale={locale}
          />
        </Box>
      )}
      <div>
        {notes.state === STATE_ERROR && (
          <ErrorAlert>
            <Text>{t("We can't show your notes right now...")}</Text>
          </ErrorAlert>
        )}
        {noteList}
      </div>
    </>
  );
};

export default observer(Notes);
